import PropTypes from 'prop-types';
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';

import DetectRTC from 'detectrtc';

import { Map, Iterable }  from 'immutable';
import isEmpty            from 'lodash/isEmpty';

import Alert      from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box        from '@mui/material/Box';
import Button     from '@mui/material/Button';
import Grid       from '@mui/material/Grid';
import Modal      from '@mui/material/Modal';
import Link       from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import grey                 from '@mui/material/colors/grey';
import CameraAlt            from '@mui/icons-material/CameraAlt';
import Stop                 from '@mui/icons-material/Stop';
import PlayCircle           from '@mui/icons-material/PlayCircle';
import Replay               from '@mui/icons-material/Replay';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';

import Display    from 'components/Generic/Display';
import ModalBox   from 'components/Generic/MUI/ModalBox';
import VideoTimer from 'components/Generic/Inputs/VideoTimer';

import getSupportedMediaType from 'helpers/media_recorder';
import { isValidURL }        from 'helpers/url';

function Video({
  id = null,
  name = null,
  value = null,
  onChange = null,
  readOnly = false,
  disabled = false,
  min = 0,
  max = 60,
  rerecordable = true,
  forced,
}) {
  const [state, setState] = useState(new Map());

  const isOpenCamera   = state.get('isOpenCamera', false);
  const isRecording    = state.get('isRecording', false);
  const previewPlaying = state.get('previewPlaying', false);
  const loadedVideo    = state.get('loadedVideo', []);
  const videoGUMready  = state.get('videoGUMready', false);
  const webrtcErrors   = state.get('webrtcErrors', []);
  const webrtcWarnings = state.get('webrtcWarnings', []);
  const videourl       = state.get('videourl', '');
  const videoStream    = state.get('videoStream', '');
  const timerFinished  = state.get('timerFinished', false);
  const stoppable      = state.get('stoppable', false);

  const videoPreviewRef  = useRef();
  const videoStreamRef   = useRef();
  const videoTimerRef    = useRef();
  const recordedBuffRef  = useRef([]);
  const mediaRecorderRef = useRef(null);

  const isUploading = state.get('isUploading', false);

  const setStoppable = useCallback((flag) => setState(prev => prev.set('stoppable', flag)), []);

  function openCamera() {
    setState(state.set('isOpenCamera', true));
  }

  function closeCamera() {
    setState(state.set('isOpenCamera', false));
  }

  function startRecording() {
    setState(state.set('isRecording', true));
  }

  function stopRecording() {
    setState(state.set('isRecording', false));
  }

  function playPreview() {
    setState(state.set('previewPlaying', !state.get('previewPlaying')));
  }

  function stopMediaRecording() {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop();

    if (videoStream) {
      videoStream.getTracks().forEach((track) => {
        track.stop();
      });
    }

    setState(state.set('videoGUMready', false));
  }

  function resetAll() {
    stopMediaRecording();

    recordedBuffRef.current = [];

    setState(state.set('isRecording', false)
      .set('isOpenCamera', false)
      .set('previewPlaying', false)
      .set('loadedVideo', [])
      .set('signedBlobId', '')
      .set('videourl', '')
      .set('videoGUMready', false)
      .set('isUploading', false));

    onChange(name, new Map());
  }

  function checkCompatibility() {
    const errors = [];
    const warnings = [];

    if (!DetectRTC.isWebsiteHasMicrophonePermissions) errors.push(t('eos.landing_page.text.mic_not_permitted'));

    if (!DetectRTC.isWebsiteHasWebcamPermissions) errors.push(t('eos.landing_page.text.camera_not_permitted'));

    if (!DetectRTC.hasMicrophone) errors.push(t('eos.landing_page.text.no_mic'));

    if (!DetectRTC.hasWebcam) errors.push(t('eos.landing_page.text.no_camera'));

    const mimeType = getSupportedMediaType('video');

    if (isEmpty(mimeType)) warnings.push(t('eos.landing_page.text.no_supported_mimetype'));

    return [errors, warnings];
  }

  useEffect(() => {
    // For view only
    if (value === null) return;

    let url = '';
    if (!isEmpty(value.get('blob'))) {
      url = window.URL.createObjectURL(value.get('blob')[0]);
    } else if (isValidURL(value.get('url'))) {
      url = value.get('url');
    } else return;

    setState(state.set('isUploading', false));

    videoPreviewRef.current.src = url;
    videoPreviewRef.current.pause();
  }, [value]);

  useEffect(() => {
    if (!isOpenCamera) {
      stopMediaRecording();
      return;
    }

    if (navigator.mediaDevices === undefined) {
      setState(state.set('webrtcErrors', [t('eos.landing_page.text.no_media_device')]));
      return;
    }

    const constraints = { audio: true, video: true };

    navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
      DetectRTC.load(() => {
        const [errors, warnings] = checkCompatibility();

        if (errors.length > 0) {
          setState(state.set('webrtcErrors', errors));
          return;
        }

        videoStreamRef.current.srcObject = stream;

        setState(state.set('videoGUMready', true)
          .set('videoStream', stream)
          .set('webrtcWarnings', warnings));
      });
    }).catch(() => {
      setState(state.set('webrtcErrors', [t('eos.landing_page.text.generic_browser_error')]));
    });
  }, [isOpenCamera]);

  useEffect(() => {
    if (isRecording) {
      const mimeType = getSupportedMediaType('video');

      try {
        mediaRecorderRef.current = new MediaRecorder(
          videoStream,
          { mimeType, audioBitsPerSecond: 64000, videoBitsPerSecond: 312500 },
        );

        mediaRecorderRef.current.addEventListener('dataavailable', (e) => {
          recordedBuffRef.current.push(e.data);
        });

        mediaRecorderRef.current.start(10);

        videoTimerRef.current.start(recordedBuffRef.current);
      } catch (e) {
        setState(state.set('webrtcErrors', [e.message]));
      }
    } else {
      if (recordedBuffRef.current.length === 0) return;

      if (videoTimerRef.current) {
        videoTimerRef.current.stop();
      }

      // create blob
      const blob = new Blob(recordedBuffRef.current, { type: mediaRecorderRef.current.mimeType });
      const src = window.URL.createObjectURL(blob);

      setState(state.set('loadedVideo', [blob])
        .set('videourl', src)
        .set('isOpenCamera', false)
        .set('isUploading', true));

      videoPreviewRef.current.src = src;
      videoPreviewRef.current.pause();
    }
  }, [isRecording]);

  useEffect(() => {
    if (isEmpty(videoPreviewRef.current)) return;

    if (previewPlaying) {
      videoPreviewRef.current.play();
      return;
    }

    videoPreviewRef.current.pause();
  }, [previewPlaying]);

  useEffect(() => {
    if (isEmpty(loadedVideo)) return;

    onChange(name, new Map({ blob: loadedVideo, incomplete: !stoppable, forced: timerFinished }));
  }, [loadedVideo]);

  useEffect(() => {
    if (!forced) return

    if (isRecording) {
      setState(state.set('timerFinished', true)); // Mid recording
      return;
    }

    if (value && value.get('blob')) { // Answer present
      onChange(name, new Map({ forced: true }));
    }

    onChange(name, new Map({ blob: null, duration: 0, text: t('eos.landing_page.label.no_answer'), forced: true, incomplete: true })); // Candidate didn't record once
  }, [forced]);

  useEffect(() => {
    if (timerFinished && isRecording) {
      stopRecording();
    }
  }, [timerFinished, isRecording]);

  function Errors() {
    if (webrtcErrors.length === 0) return null;

    return (
      <Alert data-testid="alert-error" severity="error" sx={{ mt: 2 }}>
        <AlertTitle>{t('eos.landing_page.label.error')}</AlertTitle>
        <ul style={{ padding: 0, margin: 0 }}>
          {
            webrtcErrors.map((msg) => <li key={msg}>{msg}</li>)
          }
        </ul>
      </Alert>
    );
  }

  function Warnings() {
    if (webrtcWarnings.length === 0) return null;

    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        <AlertTitle>{t('eos.landing_page.label.warning')}</AlertTitle>
        <ul style={{ padding: 0, margin: 0 }}>
          {
            webrtcWarnings.map((msg) => <li key={msg}>{msg}</li>)
          }
        </ul>
      </Alert>
    );
  }

  function FallbackDownload() {
    if (videourl.length === 0) return null;

    return (
      <Typography variant="p" py={2}>
        {t('eos.landing_page.text.trouble_playing_response')}
        <Link href={videourl} download={`response-${Date.now()}.webm`} underline="none" target="_blank" rel="noreferrer" sx={{ ml: 1 }}>
          {t('eos.landing_page.label.click_download')}
        </Link>
      </Typography>
    );
  }

  // Value could be the signedBlobId, in case of the response of the answer or can be a URL
  const showPreview = !isRecording && (loadedVideo.length > 0 || value);
  return (
    <Grid item xs={12}>
      <Box sx={{
        display: 'flex', flexDirection: 'column', minHeight: 100, width: '100%', border: 1, borderRadius: 1, justifyContent: 'center', alignItems: 'center', mt: 2, borderColor: grey[400], p: 2,
      }}
      >
        <video
          ref={videoPreviewRef}
          style={{ display: showPreview ? null : 'none', width: '100%', maxHeight: '240px' }}
          controls
          playsInline
          height="240"
          data-testid="replay"
        >
          <track kind="captions" />
        </video>
        <Display if={showPreview}>
          <FallbackDownload />
          <Grid
            container
            item
            spacing={1}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              'button': {
                width: '100%'
              }
            }}
          >
            <Grid item md={3} xs={12} sx={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <Button variant="outlined" color={previewPlaying ? 'error' : 'primary'} onClick={() => playPreview()} disabled={disabled} startIcon={previewPlaying ? <Stop /> : <PlayCircle />} sx={{ display: 'flex' }} data-testid="play-pause">
                {previewPlaying ? t('eos.landing_page.label.pause') : t('eos.landing_page.label.play_recording')}
              </Button>
            </Grid>

            <Display if={rerecordable}>
              <Grid item md={3} xs={12} sx={{
                display: 'flex',
                justifyContent: 'flex-start'
              }}>
                <Button variant="outlined" color="primary" onClick={() => resetAll()} startIcon={<Replay />} disabled={disabled} data-testid="new-recording">
                  {t('eos.landing_page.label.new_recording')}
                </Button>
              </Grid>
            </Display>
          </Grid>
        </Display>
        <Display if={!isRecording && isEmpty(videourl)}>
          <Button variant="contained" color="primary" onClick={() => openCamera()} startIcon={<CameraAlt />} sx={{ display: 'flex' }} disabled={disabled} data-testid="start-camera">{t('eos.landing_page.label.start_camera')}</Button>
        </Display>
      </Box>
      <Display if={isUploading}>
        <Typography variant="p" sx={{ fontStyle: 'italic', mt: 1, textAlign: 'center' }}>
          {t('eos.landing_page.label.upload_time')}
        </Typography>
      </Display>

      <Modal
        open={isOpenCamera}
        onClose={() => closeCamera()}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        data-testid="modal"
      >
        <ModalBox>
          <Box sx={{
            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
          }}
          >
            <video
              ref={videoStreamRef}
              playsInline
              autoPlay
              muted
              height="240"
              data-testid="livecamera"
              style={{ width: '100%' }}
            />
            <VideoTimer ref={videoTimerRef} stopCallback={stopRecording} min={min} max={max} setStoppable={setStoppable}/>
            <Display if={!isRecording}>
              <Button disabled={!videoGUMready} variant="outlined" color="primary" onClick={() => startRecording()} startIcon={<VideoCameraFrontIcon />} sx={{ mt: 2 }} data-testid="start-recording">{t('eos.landing_page.label.start_recording')}</Button>
            </Display>
            <Display if={isRecording}>
              <Button variant="outlined" color="error" disabled={!stoppable} onClick={() => stopRecording()} startIcon={<Stop />} sx={{ mt: 2 }} data-testid="stop">{t('eos.landing_page.label.stop')}</Button>
            </Display>
            <Errors />
            <Warnings />
          </Box>
        </ModalBox>
      </Modal>
    </Grid>
  );
}

Video.propTypes = {
  id:           PropTypes.string,
  name:         PropTypes.string,
  value:        PropTypes.objectOf(Iterable), // URL or signedBlobId
  onChange:     PropTypes.func,
  readOnly:     PropTypes.bool,
  disabled:     PropTypes.bool,
  min:          PropTypes.number,
  max:          PropTypes.number,
  rerecordable: PropTypes.bool,
};

export default Video;
